// Cliente de la API del portal.
//
// Autentica con la cookie de sesión (no con API key: son mecanismos separados
// y no intercambiables, ADR-019). La cookie es HttpOnly, así que este código
// nunca la ve; `credentials: "include"` deja que el navegador la mande.
//
// El token CSRF sí es legible a propósito: es el patrón double-submit y hay
// que devolverlo en una cabecera.

export type Envelope<T> = { data: T; meta?: { next_cursor?: string; count?: number } };

export type ApiError = {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  request_id?: string;
};

/** Error de la API con el código del catálogo, para poder discriminar. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
    this.name = "ApiFailure";
  }

  /** La sesión caducó o no es válida: hay que volver al login. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)iamedia_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

/** Clave de idempotencia por operación, como exige el contrato. */
function idempotencyKey(): string {
  return `portal-${crypto.randomUUID()}`;
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Se reusa una clave concreta solo si quien llama necesita reintentar. */
  idempotencyKey?: string;
};

let onUnauthenticated: (() => void) | null = null;

/**
 * Registra qué hacer cuando la sesión caduca.
 *
 * Centralizarlo evita que cada vista tenga que acordarse de redirigir, que es
 * cómo se termina con pantallas que muestran un error en vez de llevar al
 * login.
 */
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

let onMFAEnrollmentRequired: (() => void) | null = null;

/**
 * Registra qué hacer cuando el rol exige segundo factor y el usuario no lo
 * tiene.
 *
 * El backend responde `403 MFA_ENROLLMENT_REQUIRED` en toda la API de negocio
 * hasta que se configure. Sin esto, el portal mostraría un 403 en cada pantalla
 * y la persona no sabría que lo que falta es el alta del segundo factor.
 */
export function setMFAEnrollmentHandler(handler: () => void): void {
  onMFAEnrollmentRequired = handler;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const sobre = await requestEnvelope<Envelope<T> | undefined>(path, options);
  // Un 204 no trae sobre: lo devuelven los DELETE que no tienen nada que
  // contar. `T` es `void` en esos casos.
  return sobre === undefined ? (undefined as T) : sobre.data;
}

/**
 * requestEnvelope hace la petición y devuelve el **sobre completo**.
 *
 * `request` y `requestPage` son dos vistas de lo mismo, y el manejo de errores
 * —sesión caducada, MFA obligatorio, el cuerpo de error de la API— tiene que
 * estar en un solo sitio: duplicarlo es cómo un camino acaba sin cerrar la
 * sesión cuando el otro sí.
 */
async function requestEnvelope<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (method !== "GET") {
    headers["X-CSRF-Token"] = csrfToken();
  }
  if (method === "POST") {
    headers["Idempotency-Key"] = options.idempotencyKey ?? idempotencyKey();
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const parsed = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const failure = new ApiFailure(
      response.status,
      (parsed.error as ApiError) ?? { code: "UNKNOWN", message: response.statusText },
    );
    if (failure.isUnauthenticated) {
      onUnauthenticated?.();
    }
    if (failure.error.code === "MFA_ENROLLMENT_REQUIRED") {
      onMFAEnrollmentRequired?.();
    }
    throw failure;
  }

  // El **sobre entero**, no `parsed.data`. Quien llama decide qué quiere de
  // él: `request` se queda con `data` y `requestPage` además con el cursor.
  return parsed as T;
}

/** Una página de resultados: los datos y el cursor de la siguiente, si hay. */
export type Page<T> = { data: T; nextCursor?: string };

/**
 * requestPage es como `request` pero **conserva el `meta`**.
 *
 * Hace falta un camino aparte porque `request` devuelve `data` y tira el resto
 * del sobre, y el cursor vive en `meta.next_cursor`. Sin esto el portal no
 * podía paginar: pedía la primera página y no tenía forma de saber si había
 * más.
 */
export async function requestPage<T>(path: string): Promise<Page<T>> {
  const sobre = await requestEnvelope<Envelope<T> | undefined>(path);
  if (sobre === undefined) return { data: undefined as T };
  const cursor = sobre.meta?.next_cursor;
  return cursor !== undefined && cursor !== ""
    ? { data: sobre.data, nextCursor: cursor }
    : { data: sobre.data };
}

/**
 * requestText manda un cuerpo que no es JSON.
 *
 * Lo necesita la importación de tarifas: el cuerpo es un CSV. Se mantiene
 * aparte de `request` en lugar de añadirle un modo porque la diferencia no es
 * solo el `Content-Type` — un CSV de cincuenta mil líneas no se serializa con
 * `JSON.stringify`, y meterlo en el mismo camino invitaría a hacerlo.
 */
export async function requestText<T>(
  path: string,
  contentType: string,
  body: string,
): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "Content-Type": contentType,
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "include",
    body,
  });

  const text = await response.text();
  const parsed = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const failure = new ApiFailure(
      response.status,
      (parsed.error as ApiError) ?? { code: "UNKNOWN", message: response.statusText },
    );
    if (failure.isUnauthenticated) {
      onUnauthenticated?.();
    }
    throw failure;
  }
  return (parsed as Envelope<T>).data;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  /** Como `get`, pero devuelve también el cursor de la página siguiente. */
  getPage: <T>(path: string) => requestPage<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body }),
  // DELETE con cuerpo existe por el borrado de datos de una cuenta: la
  // confirmación va dentro del cuerpo a propósito, para que no pueda dispararse
  // con una URL suelta. Devuelve `T` porque ese endpoint responde con el
  // conteo de lo que borró.
  delete: <T = void>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body }),
  postText: requestText,
};
